import * as Popover from "@radix-ui/react-popover";
import { MouseEvent, PointerEvent, useEffect, useRef, useState } from "react";

/** A press on one of these is its own: typing, picking, clicking. */
const CONTROLS =
  "input, textarea, select, button, a, label, [contenteditable], [role='button'], [role='combobox']";

/** The room a moved pop-out keeps from the window's edges, in pixels. */
const EDGE = 8;

const clamp = (value: number, least: number, most: number) =>
  Math.max(least, Math.min(value, most));

type Drag = {
  pointerX: number;
  pointerY: number;
  /** Where the pop-out was, and its shift then. */
  rect: DOMRect;
  x: number;
  y: number;
};

/**
 * A pop-out's content (Popover.Content: the calendar's quick Book Meeting, a
 * board card's New task) that the person can drag anywhere in the window,
 * by its header (an element marked data-drag-handle) or its padding, to
 * move it off what it covers. Presses on its fields and buttons stay
 * theirs. It opens where it always did; a move shifts it from there until
 * `resetKey` changes (a new pop-out). A moved pop-out stays inside the
 * window as it is dragged, as it grows, and when the window is resized.
 * A move re-renders only this, not the form inside it.
 */
export default function DraggableContent({
  resetKey,
  style,
  ...props
}: Popover.PopoverContentProps & {
  /** Another card or time: the pop-out opens unmoved. */
  resetKey: string | number;
}) {
  const [shift, setShift] = useState({ key: resetKey, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const drag = useRef<Drag | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  let { x, y } = shift;
  if (shift.key !== resetKey) {
    setShift({ key: resetKey, x: 0, y: 0 });
    x = 0;
    y = 0;
  }

  // When a moved pop-out grows (a person added, a taller description) or
  // the window shrinks, the move gives way only as far as it must for the
  // pop-out to stay inside the window. Radix keeps an unmoved one inside.
  useEffect(() => {
    const content = ref.current;
    if (!content) return;
    let frame = 0;
    const keepInside = () => {
      cancelAnimationFrame(frame);
      // A frame later, once Radix has placed it for the new size.
      frame = requestAnimationFrame(() =>
        setShift((s) => {
          if (!s.x && !s.y) return s;
          const r = content.getBoundingClientRect();
          const dx =
            clamp(r.left, EDGE, window.innerWidth - r.width - EDGE) - r.left;
          const dy =
            clamp(r.top, EDGE, window.innerHeight - r.height - EDGE) - r.top;
          return dx || dy ? { ...s, x: s.x + dx, y: s.y + dy } : s;
        })
      );
    };
    const observer = new ResizeObserver(keepInside);
    observer.observe(content);
    window.addEventListener("resize", keepInside);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", keepInside);
    };
  }, []);

  const end = () => {
    drag.current = null;
    setDragging(false);
  };

  return (
    <Popover.Content
      {...props}
      ref={ref}
      data-dragging={dragging || undefined}
      style={{ ...style, translate: `${x}px ${y}px` }}
      onPointerDown={(e: PointerEvent<HTMLDivElement>) => {
        const content = e.currentTarget;
        const target = e.target as Element;
        // React also brings presses from portals opened inside it.
        if (e.button !== 0 || !content.contains(target)) return;
        const onHandle =
          target === content || !!target.closest("[data-drag-handle]");
        if (!onHandle || target.closest(CONTROLS)) return;
        content.setPointerCapture(e.pointerId);
        // An open menu or calendar inside closes, as a press anywhere else
        // would close it, rather than ride along past the window's edge:
        // the select's menu on blur, the date picker's calendar on the
        // mousedown that follows (so that is not cancelled here).
        const focused = document.activeElement;
        if (focused instanceof HTMLElement && content.contains(focused)) {
          focused.blur();
        }
        drag.current = {
          pointerX: e.clientX,
          pointerY: e.clientY,
          rect: content.getBoundingClientRect(),
          x,
          y,
        };
        setDragging(true);
      }}
      onMouseDown={(e: MouseEvent<HTMLDivElement>) => {
        // No text selection while moving.
        if (drag.current) e.preventDefault();
      }}
      onPointerMove={(e: PointerEvent<HTMLDivElement>) => {
        const start = drag.current;
        if (!start) return;
        const { rect } = start;
        const left = clamp(
          rect.left + e.clientX - start.pointerX,
          EDGE,
          window.innerWidth - rect.width - EDGE
        );
        const top = clamp(
          rect.top + e.clientY - start.pointerY,
          EDGE,
          window.innerHeight - rect.height - EDGE
        );
        setShift({
          key: resetKey,
          x: start.x + left - rect.left,
          y: start.y + top - rect.top,
        });
      }}
      onPointerUp={end}
      onPointerCancel={end}
    />
  );
}
